import React, { useState, useEffect } from 'react';
import SideBar from './Components/SideBar';
import CustomButton from './Components/Buttons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatabaseUtil from './Util/DatabaseUtil';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function DashRelatedSummary() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRole, setSelectedRole] = useState('Student');
    const [temperatureData, setTemperatureData] = useState({});
    const [categoryStats, setCategoryStats] = useState({ views: {}, watchtime: {} });


    useEffect(() => {
        loadTemperatureData();
        loadCategoryStats();
    }, [selectedRole, i18n.language]);

    const loadTemperatureData = async () => {
        try {
            setLoading(true);
            const data = await DatabaseUtil.getTemperatureData(selectedRole, i18n.language);
            setTemperatureData(data);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };
    const loadCategoryStats = async () => {
        var roleSelected= selectedRole;
        try {
            if(selectedRole=== 'student' || selectedRole === 'Student')
            {
                roleSelected= 'LELwD'
            }
            const stats = await DatabaseUtil.getCategoryStats(roleSelected);
            setCategoryStats(stats);
        } catch (err) {
            setError(err.message);
        }
    };
    
    const generateCategoryViewsChartData = () => {
        return {
            labels: Object.keys(categoryStats.views).map(key => `Topic ${key}`),
            datasets: [{
                label: t('totalVideoViewsTopic'),
                data: Object.values(categoryStats.views),
                backgroundColor: '#ffffff',
                borderColor: '#ffffff',
                borderWidth: 0,
            }]
        };
    };
    
    const generateCategoryWatchtimeChartData = () => {
        return {
            labels: Object.keys(categoryStats.watchtime).map(key => `Topic ${key}`),
            datasets: [{
                label: t('avgWatchtimeTopic'),
                data: Object.values(categoryStats.watchtime),
                backgroundColor: '#ffffff',
                borderColor: '#ffffff',
                borderWidth: 0,
            }]
        };
    };

    const roles = ['Student', 'Caregiver', 'Teacher', 'Classmate'];
    const rolesLower = ['student', 'caregiver', 'teacher', 'classmate']; 


    const generateBarChartData = () => {
        const filteredData = Object.entries(temperatureData);
        const currentTopicOrder = i18n.language === 'es' ? topicOrderESP : topicOrder;
    
        const topics = filteredData.map(([topic]) => topic);
        const weights = filteredData.map(([_, weight]) => weight);
    
        const labels = topics.map(topic => `Topic ${currentTopicOrder[topic]}`);
    
        return {
            labels: labels,
            datasets: [
                {
                    label: t('temperature'),
                    data: weights,
                    backgroundColor: '#ffffff',
                    borderColor: '#ffffff',
                    borderWidth: 0,
                },
            ],
        };
    };

    const topicOrder = {
        "Linguistic competence and interaction:": 1,
        "Cultural awareness:": 2,
        "Cultural competence and understanding of the latin perspective:": 3,
        "Cultural competence and understanding of the american perspective:": 4,
        "Disability knowledge and awareness:": 5,
        "Representation:": 6,
        "Inclusive communication:": 7,
        "Empathy and sensitivity training:": 8,
        "Social integration skills:": 9,
        "Parent and caregiver involvement:": 10,
        "Cultural representation in curriculum:": 11,
        "Anti-bias education:": 12,
        "Legal and ethical understanding:": 13,
        "Peer support and mentoring:": 14,
        "Professional development and resources for educators:": 15
    };

    const topicOrderESP = {
        "Competencia lingüística e interacción:": 1,
        "Conciencia cultural:": 2,
        "Competencia cultural y comprensión de la perspectiva latina:": 3,
        "Competencia cultural y comprensión de la perspectiva estadounidense:": 4,
        "Conocimiento y conciencia sobre la discapacidad:": 5,
        "Representación:": 6,
        "Comunicación inclusiva:": 7,
        "Entrenamiento de Empatía y Sensibilidad:": 8,
        "Habilidades de integración social:": 9,
        "Participación de padres y cuidadores:": 10,
        "Representación Cultural en el Currículo:": 11,
        "Educación contra prejuicios:": 12,
        "Comprensión legal y ética:": 13,
        "Apoyo de pares y tutoría:": 14,
        "Desarrollo profesional y recursos para educadores:": 15
    };

    const chartOptions = {
        plugins: {
            legend: {
                labels: {
                    color: '#ffffff', 
                    font: {
                        size: 16,
                        family: 'KomikaAxis',
                    },
                },
            },
            tooltip: {
                titleColor: '#ffffff', 
                bodyColor: '#ffffff',  
                
            },
            datalabels: {
                color: '#ffffff',
                anchor: 'end',
                align: 'end',
                formatter: (value) => value.toFixed(1),
                font: {
                    size: 10,
                    family: 'KomikaAxis'
                },
            },
        },
        scales: {
            x: {
                ticks: {
                    color: '#ffffff',
                    font: {
                        size: 12,
                        family: 'KomikaAxis'
                    },
                },
                
            },
            y: {
                ticks: {
                    color: '#ffffff', 
                    font: {
                        size: 13,
                        family: 'KomikaAxis'
                    },
                },
                
                suggestedMax: Math.max(...Object.values(temperatureData))+2, //So the number on highest bar doesn't clip
            },

        },
    };
    
    const watchtimeChartOptions = {
        ...chartOptions,
        scales: {
            ...chartOptions.scales,
            y: {
                ...chartOptions.scales.y,
                max: 100,
            }
        }
    };

    return (
        <>
            <SideBar />
            <div style={{ padding: '20px', fontFamily: 'PlaypenSansVar', marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '60%' }}>
                <h2 style={{ fontFamily: 'KomikaAxis', color: 'var(--primary-color)'}}>{t('relatedTopics')}</h2>

                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                {roles.map((role, index) => (
            <CustomButton
                key={role}
                label={t(rolesLower[index])}
                onClick={() => setSelectedRole(role.charAt(0).toUpperCase() + role.slice(1))}
                type={selectedRole.toLowerCase() === role.toLowerCase() ? "secondary" : "primary"}
                style={{
                    padding: '10px 20px',
                    fontSize: '16px'
                        }}
                    />
                ))}
                </div>
                {loading ? (
                    <p>Loading...</p>
                ) : error ? (
                    <p style={{ color: 'red' }}>{error}</p>
                ) : (
                    <>
                        <div style={{ 
                            backgroundColor: 'var(--primary-color)', 
                            padding: '20px',
                            borderRadius: '8px',
                            color: 'white',
                            width: '100%',
                           /* maxWidth: '800px',*/
                            marginBottom: '20px'
                        }}>
                            <ol style={{ 
                                /*paddingLeft: '40px',*/
                                fontSize: '14px',
                                lineHeight: '1.5',
                                listStyleType: 'none'
                            }}>
                                {Object.entries(temperatureData)
                                    .map(([topic, weight], index) => (
                            <li key={index} style={{
                                marginBottom: '10px',
                                textAlign: 'left'
                            }}>
                            {`(${t('topic')} ${(i18n.language === 'es' ? topicOrderESP : topicOrder)[topic]}) ${topic}`} <strong>{weight.toFixed(1)}</strong></li>
                                    ))}
                            </ol>
                        </div>
                        <div style={{ 
                            backgroundColor: 'var(--primary-color)', 
                            padding: '20px',
                            borderRadius: '8px',
                            color: 'white',
                            width: '100%',
                           /* maxWidth: '800px',*/
                            marginBottom: '5px'
                        }}>
                        
                        <Bar data={generateBarChartData()} options={chartOptions} plugins={[ChartDataLabels]}/>
                        <div 
                        style={{
                            borderTop: '5px solid #FFFFFF',
                            borderRadius: '5px',   
                            margin: '5px 0',
                        }}
                           ></div>
                        <div style={{ 
                            backgroundColor: 'var(--primary-color)', 
                            padding: '20px',
                            borderRadius: '8px',
                            color: 'white',
                           /* maxWidth: '800px',*/
                            marginBottom: '5px'
                        }}>
                       
                        <Bar 
                            data={generateCategoryViewsChartData()} 
                            options={chartOptions} 
                            plugins={[ChartDataLabels]}
                        />
                         <div 
                        style={{
                            borderTop: '5px solid #FFFFFF',
                            borderRadius: '5px',   
                            margin: '5px 0',
                        }}
                           ></div>
                    
                    <div style={{ 
                        backgroundColor: 'var(--primary-color)', 
                        padding: '20px',
                        borderRadius: '8px',
                        color: 'white',
                        /*maxWidth: '800px',*/
                        marginBottom: '5px'
                    }}>
                       
                        <Bar 
                            data={generateCategoryWatchtimeChartData()} 
                            options={watchtimeChartOptions} 
                            plugins={[ChartDataLabels]}
                        />
                    </div>
                        </div>
                        </div>
    
                    </>
                )}
                </div>
            </div>
        </>
    );
}

export default DashRelatedSummary;
